import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { gsap, Power1 } from 'gsap';

import Tooltip, { TooltipColors } from '@/shared/components/tooltip/Tooltip';

type StarProgressProps = {
	total: number;
	earned: number;
	previousEarned?: number;
	withAnimation?: boolean;
	showAnimation?: boolean;
	onAnimationComplete?: () => void;
	tooltipOpen?: boolean;
};

const getStarsSteps = (total: number): number[] => {
	const safeTotal = Math.max(total, 1);
	const steps = [];
	const stepSize = Math.ceil(safeTotal / 4);

	for (let i = 0; i <= safeTotal; i += stepSize) {
		steps.push(i);
	}

	if (steps[steps.length - 1] !== safeTotal) {
		steps.push(safeTotal);
	}

	return steps;
};

const StarProgress = ({
	total,
	earned,
	previousEarned = 0,
	withAnimation,
	showAnimation,
	onAnimationComplete,
	tooltipOpen,
}: StarProgressProps) => {
	const { t } = useTranslation();
	const fillRef = useRef<HTMLDivElement | null>(null);
	const circleRef = useRef<HTMLDivElement | null>(null);
	const earnedRef = useRef<HTMLSpanElement>(null);

	const safeTotal = Math.max(total, 1);
	const safeEarned = Math.max(0, Math.min(earned, safeTotal));

	const anchorId = useMemo(() => `star-progress-anchor-${Math.random().toString(36).slice(2, 10)}`, []);
	const tooltipId = useMemo(() => `star-progress-tooltip-${Math.random().toString(36).slice(2, 10)}`, []);

	const tooltipLabel = useMemo(
		() =>
			t('free_play.category.stars_needed', {
				count: Math.max(0, safeTotal - safeEarned),
			}),
		[safeEarned, safeTotal, t],
	);

	const steps = useMemo(() => getStarsSteps(safeTotal), [safeTotal]);
	const earnedPercentage = useMemo(() => Math.min(safeEarned / safeTotal, 1) * 100, [safeEarned, safeTotal]);

	useEffect(() => {
		if (!withAnimation) {
			return;
		}

		const previousEarnedPercentage = Math.min(Math.max(previousEarned, 0) / safeTotal, 1) * 100;

		gsap.set(fillRef.current, { width: `${previousEarnedPercentage}%` });
		gsap.set(circleRef.current, { left: `${previousEarnedPercentage}%` });

		if (earnedRef.current) {
			earnedRef.current.textContent = Math.floor(previousEarned).toString();
		}
	}, [previousEarned, safeTotal, withAnimation]);

	useEffect(() => {
		if (!withAnimation || !showAnimation) {
			return;
		}

		const animatedValue = { value: previousEarned };

		gsap.to(fillRef.current, {
			width: `${earnedPercentage}%`,
			duration: 1,
			delay: 0.5,
			ease: Power1.easeInOut,
		});

		gsap.to(circleRef.current, {
			left: `${earnedPercentage}%`,
			duration: 1,
			delay: 0.5,
			ease: Power1.easeInOut,
		});

		gsap.to(animatedValue, {
			value: safeEarned,
			duration: 1,
			delay: 0.5,
			ease: Power1.easeInOut,
			onUpdate: () => {
				if (earnedRef.current) {
					earnedRef.current.textContent = Math.floor(animatedValue.value).toString();
				}
			},
			onComplete: () => {
				onAnimationComplete?.();
			},
		});
	}, [earnedPercentage, onAnimationComplete, previousEarned, safeEarned, showAnimation, withAnimation]);

	return (
		<div className="star-progress">
			<div className="star-progress__bar">
				<div className="star-progress__fill" style={{ width: `${earnedPercentage}%` }} ref={fillRef} />
				<div className="star-progress__dots">
					{steps.map((step) => (
						<span key={step} className={classNames('star-progress__dot', step <= safeEarned && 'filled')} />
					))}
				</div>
				<div
					id={anchorId}
					className="star-progress__circle"
					style={{ left: `${earnedPercentage}%` }}
					ref={circleRef}
					role="img"
					aria-label={tooltipLabel}
					tabIndex={0}
				>
					<span className="icon icon-star-filled" />
					<Tooltip
						id={tooltipId}
						title={tooltipLabel}
						anchor={anchorId}
						place="bottom"
						color={TooltipColors.DARK}
						className="star-progress__tooltip"
						isOpen={tooltipOpen}
					/>
				</div>
			</div>
			<div className="star-progress__numbers">
				<span
					className={classNames('star-progress__earned', safeEarned === 0 && 'star-progress__earned--light')}
				>
					<span ref={earnedRef}>{safeEarned}</span>/
				</span>
				<span className="star-progress__total">{safeTotal}</span>
			</div>
		</div>
	);
};

export default StarProgress;
